import { Reveal, Eyebrow } from "./Reveal";
import {
  Scale,
  Landmark,
  DatabaseZap,
  GraduationCap,
  ArrowUpRight,
} from "lucide-react";
import { scrollToId } from "./Header";

const IMG_DESK =
  "https://images.unsplash.com/photo-1671466357089-d0d66d6c47ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjBsYXclMjBvZmZpY2UlMjBzaWduYXR1cmUlMjBmb3VudGFpbiUyMHBlbiUyMG1hcmJsZSUyMGRlc2t8ZW58MHx8fHwxNzkxNzM2MzQ0fDA&ixlib=rb-4.1.0&q=85";
const IMG_INK =
  "https://images.unsplash.com/photo-1592781920723-2d37095ec0ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsdXh1cnklMjBsYXclMjBvZmZpY2UlMjBzaWduYXR1cmUlMjBmb3VudGFpbiUyMHBlbiUyMG1hcmJsZSUyMGRlc2t8ZW58MHx8fHwxNzkxNzM2MzQ0fDA&ixlib=rb-4.1.0&q=85";

const DOMAINS = [
  {
    n: "01",
    icon: Scale,
    title: "Droit des sociétés",
    desc: "Accompagnement juridique des entreprises : création et structuration, rédaction et revue de contrats types, sécurisation de vos activités.",
    tags: ["Création & structuration", "Contrats types"],
    span: "lg:col-span-7",
    img: IMG_DESK,
  },
  {
    n: "02",
    icon: Landmark,
    title: "Conformité bancaire",
    desc: "Compliance et conformité réglementaire : mise en conformité, cartographie et gestion des risques, procédures internes. Dispositifs LCB-FT/KYC : prévention du blanchiment et du financement du terrorisme, appui documentaire et contrôles de premier et deuxième niveau.",
    tags: ["Compliance réglementaire", "LCB-FT / KYC", "Contrôles 1er & 2ᵉ niveau"],
    span: "lg:col-span-5",
  },
  {
    n: "03",
    icon: DatabaseZap,
    title: "Conformité numérique",
    desc: "Gouvernance des données et du numérique : RGPD, AI Act, Data Act, accompagnement DPO, mise en conformité des traitements de données et sensibilisation des équipes.",
    tags: ["RGPD", "AI Act", "Accompagnement DPO"],
    span: "lg:col-span-5",
    img: IMG_INK,
  },
  {
    n: "04",
    icon: GraduationCap,
    title: "Formation & sensibilisation",
    desc: "Conception et animation de formations et d'ateliers adaptés aux besoins spécifiques de votre entreprise.",
    tags: ["Ateliers sur-mesure"],
    span: "lg:col-span-7",
  },
];

const DomainCard = ({ d, index }) => {
  const Icon = d.icon;
  return (
    <Reveal delay={index * 0.08} className={`h-full md:col-span-1 ${d.span}`}>
      <article
        data-testid={`domain-card-${index + 1}`}
        className="group relative flex h-full flex-col justify-between overflow-hidden border border-ink-line bg-ink-card p-8 transition-colors duration-500 hover:border-gold/50 lg:p-10"
      >
        {d.img && (
          <>
            <img
              src={d.img}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover opacity-15 grayscale transition-all duration-700 group-hover:scale-105 group-hover:opacity-25"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-ink-card/70 via-ink-card/85 to-ink-card" />
          </>
        )}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-gold/0 blur-3xl transition-colors duration-700 group-hover:bg-gold/10"
        />
        <div className="relative">
          <div className="flex items-start justify-between">
            <span className="flex h-12 w-12 items-center justify-center border border-ink-line text-gold transition-colors duration-500 group-hover:border-gold/60">
              <Icon className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <span className="font-mono text-xs tracking-[0.3em] text-mute/60">
              {d.n}
            </span>
          </div>
          <h3 className="mt-8 font-serif text-2xl font-medium leading-snug text-ivory sm:text-[1.7rem]">
            {d.title}
          </h3>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-mute sm:text-base">
            {d.desc}
          </p>
        </div>
        <div className="relative mt-8 flex flex-wrap gap-2">
          {d.tags.map((t) => (
            <span
              key={t}
              className="border border-ink-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-mute transition-colors duration-500 group-hover:border-gold/40 group-hover:text-ivory/80"
            >
              {t}
            </span>
          ))}
        </div>
      </article>
    </Reveal>
  );
};

export const Domains = () => (
  <section
    id="expertises"
    data-testid="domains-section"
    className="relative mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-36"
  >
    <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
      <Reveal className="lg:col-span-8">
        <Eyebrow>Nos domaines d'intervention</Eyebrow>
        <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-ivory sm:text-4xl lg:text-5xl">
          Droit des sociétés, <em className="italic text-gold">conformité bancaire</em>{" "}
          & numérique
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-4">
        <p className="text-sm leading-relaxed text-mute sm:text-base">
          Sécuriser vos activités, gérer vos obligations juridiques et
          réglementaires, prévenir et maîtriser les risques : chaque mission est
          pensée pour la taille et le secteur de votre structure.
        </p>
      </Reveal>
    </div>

    <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-12 lg:gap-5">
      {DOMAINS.map((d, i) => (
        <DomainCard key={d.n} d={d} index={i} />
      ))}
    </div>

    <Reveal delay={0.1} className="mt-10">
      <a
        href="#contact"
        data-testid="domains-contact-link"
        onClick={(e) => scrollToId(e, "#contact")}
        className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.22em] text-gold"
      >
        Un besoin spécifique ? Parlons-en
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </a>
    </Reveal>
  </section>
);
